// LWI-1549 — the wizard's stages and section tabs. Mirrors the CCI wizard
// (InvoiceSidebar / InvoiceProgressStepper) but keyed to the MSR lifecycle.

export const STAGE_DRAFT = "draft";
export const STAGE_PICK_PACK = "pick-pack";
export const STAGE_REVIEW = "review";

/**
 * LWI-1596 / LWI-1629 — the Draft stage opens on ONE tab, "Shipment and Parts":
 * everything a requester must give before Submit (the Shipment Essentials
 * fields, then the parts grid). Shipment Classification follows as tab 2
 * (LWI-1630). The two halves keep their own save/dirty keys ("essentials" →
 * PUT /attributes, "parts" → PUT /parts) so a failed half is marked and the
 * other's data is never lost; the sidebar shows them as one entry.
 */
export const SHIPMENT_KEY = "shipment";

/** Draft-stage tabs (sidebar entries). */
export const DRAFT_SECTIONS = [
  { key: SHIPMENT_KEY, label: "Shipment and Parts" },
  { key: "classification", label: "Shipment Classification" },
];

/**
 * LWI-1604 — References & Notes and Charges & Costs are no longer tabs. Their
 * fields (plus Currency) sit in an optional section under the parts grid and
 * save through PUT /attributes under this key, apart from the part lines' PUT
 * /parts. It is a save/dirty key only, never a sidebar entry: markers for it
 * show on Shipment and Parts (see sidebarKey), and it never marks it complete.
 */
export const PARTS_EXTRAS_KEY = "parts-refs";

/** Retired server 422 keys → the save key whose fields they name now (LWI-1604). */
export const RETIRED_SECTIONS = { references: "parts", charges: "parts" };

/**
 * Save/marker key → the sidebar tab it shows on. The Shipment and Parts tab
 * owns "essentials", "parts" and parts-refs (and the retired LWI-1604 keys);
 * every other key is its own tab.
 */
const SECTION_TAB = {
  essentials: SHIPMENT_KEY,
  parts: SHIPMENT_KEY,
  [PARTS_EXTRAS_KEY]: SHIPMENT_KEY,
  references: SHIPMENT_KEY,
  charges: SHIPMENT_KEY,
  "pp-final-details": "pp-customer-info",
};
export const sidebarKey = (key) => SECTION_TAB[key] || key;

/** The save keys a Draft tab is made of (all must have saved for its check mark). */
export const SAVE_KEYS_OF_TAB = {
  [SHIPMENT_KEY]: ["essentials", "parts"],
  classification: ["classification"],
};

/** Sidebar keys whose every save key has completed. */
export const completedTabs = (completedSteps = []) =>
  Object.entries(SAVE_KEYS_OF_TAB)
    .filter(([, keys]) => keys.every((k) => completedSteps.includes(k)))
    .map(([tab]) => tab);

/**
 * Pick and Pack tabs — each maps onto shipment_master_msr_pickpack.
 * LWI-1624 — Final Details is gone: its values are derived (requested pickup
 * date from the pickup window, pieces / weight / freight class from the
 * units, Shipped By from the user) and Notes moved to Customer Information.
 */
export const PICK_PACK_SECTIONS = [
  { key: "pp-customer-info", label: "Customer Information Required" },
  { key: "pp-handling-units", label: "Handling Units" },
  { key: "pp-dates", label: "Dates" },
  { key: "pp-customs-invoice", label: "Customs Invoice" },
];

/**
 * The Bill of Lading view still saves its manual shipping details under this
 * key (a save/dirty key, not a tab); markers for it show on Customer Information.
 */
export const PICK_PACK_MANUAL_KEY = "pp-final-details";
export const PICK_PACK_DATES_KEY = "pp-dates";

export const REVIEW_SECTION = { key: "review", label: "Review & Submit" };

/**
 * Record tabs — the classic Details tabs that are about the MSR as a whole
 * rather than a lifecycle stage (Exceptions, Audit Log, System Log,
 * Downloads). Always reachable once the MSR exists; read-mostly.
 */
export const STAGE_RECORD = "record";
export const RECORD_SECTIONS = [
  { key: "exceptions", label: "Exceptions" },
  { key: "audit", label: "Audit Log" },
  { key: "system-log", label: "System Log" },
  { key: "downloads", label: "Downloads" },
];

/**
 * Bill of Lading and onward — carrier / tracking from the TMS plus the manual
 * shipping details. One view for statuses 3–5; the lifecycle beyond Pick and
 * Pack is owned by the carrier, not edited here.
 */
export const STAGE_BOL = "bol";
export const BOL_SECTION = { key: "bol", label: "Bill of Lading" };

export const DRAFT_KEYS = DRAFT_SECTIONS.map((s) => s.key);
export const PICK_PACK_KEYS = PICK_PACK_SECTIONS.map((s) => s.key);
/** Every save/dirty key a Pick and Pack save clears. */
export const PICK_PACK_SAVE_KEYS = [...PICK_PACK_KEYS, PICK_PACK_MANUAL_KEY];
export const RECORD_KEYS = RECORD_SECTIONS.map((s) => s.key);

/** Which stage a section key belongs to (null for an unknown key). */
export const stageOf = (sectionKey) => {
  if (DRAFT_KEYS.includes(sectionKey)) return STAGE_DRAFT;
  if (PICK_PACK_KEYS.includes(sectionKey)) return STAGE_PICK_PACK;
  if (sectionKey === REVIEW_SECTION.key) return STAGE_REVIEW;
  if (sectionKey === BOL_SECTION.key) return STAGE_BOL;
  if (RECORD_KEYS.includes(sectionKey)) return STAGE_RECORD;
  return null;
};

/** Header sections all save through PUT msr/{n}/attributes. */
export const HEADER_SECTIONS = ["essentials", "classification", PARTS_EXTRAS_KEY];

export const isHeaderSection = (key) => HEADER_SECTIONS.includes(key);

/** Is this save/dirty key part of the Draft stage (by the tab it shows on)? */
export const isDraftSaveKey = (key) => stageOf(sidebarKey(key)) === STAGE_DRAFT;

/**
 * Next / previous section within a stage (null at the edges). LWI-1669 —
 * `canView` skips the sections the user may not see.
 */
export const neighbourSection = (
  sectionKey,
  direction,
  canView = () => true
) => {
  const list = (
    stageOf(sectionKey) === STAGE_PICK_PACK ? PICK_PACK_KEYS : DRAFT_KEYS
  ).filter(canView);
  const i = list.indexOf(sectionKey);
  if (i === -1) return null;
  const j = i + (direction === "next" ? 1 : -1);
  return list[j] ?? null;
};

/**
 * LWI-1595 — where the footer "Submit MSR to Pick & Pack" shows: a live draft,
 * on the last Draft step or on Review & Submit. (The sticky-bar Submit shows
 * on every section of a draft.)
 */
export const showsFooterSubmit = ({
  isDraft,
  cancelledAt,
  section,
  // LWI-1669 — the Draft steps the user can see (the last one may be hidden).
  draftKeys = DRAFT_KEYS,
}) =>
  !!isDraft &&
  !cancelledAt &&
  (section === REVIEW_SECTION.key ||
    section === draftKeys[draftKeys.length - 1]);

/** The default landing section for an MSR in a given state. */
export const defaultSectionFor = ({ isDraft, status }) => {
  if (isDraft || Number(status) <= 1) return DRAFT_KEYS[0];
  if (Number(status) === 2) return PICK_PACK_KEYS[0];
  return BOL_SECTION.key;
};
